import SmartSuggestionsOptions from './schema/options/SmartSuggestionsOptions';
import { OwsOptionsFeatureType } from './schema/OwsOptionsFeatureType';
import OwsOptionsStore, { LoadState } from './schema/OwsOptionsStore';
import DiverseEmojisOptions from './schema/options/DiverseEmojisOptions';
import GdprAdsPrefsOptions from './schema/options/GdprAdsPrefsOptions';
import SkypeNotificationOptions from './schema/options/SkypeNotificationOptions';
import SurfaceActionsOptions from './schema/options/SurfaceActionsOptions';
import WebPushNotificationsOptions from './schema/options/WebPushNotificationsOptions';
import ConfettiOptions from './schema/options/ConfettiOptions';
import CalendarSurfaceAddinsOptions from './schema/options/CalendarSurfaceAddinsOptions';
import { createStore } from 'satcheljs';
import ExternalImagesOptions from './schema/options/ExternalImagesOptions';
import EventCaptureOptions from './schema/options/EventCaptureOptions';
import LinkedInBindingDismissedOptions from './/schema/options/LinkedInBindingDismissedOptions';
import TranslationOptions from './schema/options/TranslationOptions';
import CalendarSurfaceOptions from './schema/options/CalendarSurfaceOptions';
import MentionEventNotificationsOptions from './schema/options/MentionEventNotificationsOptions';
import TxpEventNotificationsOptions from './schema/options/TxpEventNotificationsOptions';

export default createStore<OwsOptionsStore>('owsOptionsStore', {
    options: {
        /**
         * Initial values for options in the store should be "fail-safe" values in the
         *  event that the service call to fetch the options has not yet completed or failed.
         *
         * For example, while the default setting for a feature for a user that has made no
         * changes to the options may be to have it enabled, to increase coverage and
         * discoverability, we don't want to aggravate users by potentially having it
         * enabled after they explicitly disabled it.
         * These should be nulls/falses/empty arrays and the call to LoadOptions (LoadOptions.ts)
         * should be in charge to apply defaults for non-set options and fail cases
         */
        [OwsOptionsFeatureType.SmartSuggestions]: {
            feature: OwsOptionsFeatureType.SmartSuggestions,
            smartSuggestionsEnabled: false,
            browserLocationEnabled: false,
        } as SmartSuggestionsOptions,
        [OwsOptionsFeatureType.DiverseEmojis]: {
            feature: OwsOptionsFeatureType.DiverseEmojis,
            diverseEmojisSelectedSkinTone: '',
        } as DiverseEmojisOptions,
        [OwsOptionsFeatureType.SurfaceActions]: {
            feature: OwsOptionsFeatureType.SurfaceActions,
            readSurfaceActions: [],
            readSurfaceAddins: [],
            composeSurfaceActions: [],
            composeSurfaceAddins: [],
        } as SurfaceActionsOptions,
        [OwsOptionsFeatureType.SkypeNotifications]: {
            feature: OwsOptionsFeatureType.SkypeNotifications,
            skypeMessageNotification: 1, // Toast and Sound
            skypeCallingNotification: 1, // Toast and Sound
        } as SkypeNotificationOptions,
        [OwsOptionsFeatureType.WebPushNotifications]: {
            feature: OwsOptionsFeatureType.WebPushNotifications,
            enabled: false,
            applicationServerKey: null,
        } as WebPushNotificationsOptions,
        [OwsOptionsFeatureType.Confetti]: {
            feature: OwsOptionsFeatureType.Confetti,
            confettiEnabled: false,
        } as ConfettiOptions,
        [OwsOptionsFeatureType.GdprAdsPrefs]: {
            feature: OwsOptionsFeatureType.GdprAdsPrefs,
            consentString: null,
            allowStorageAndAccess: false,
            allowPersonalization: false,
            allowAdSelectionDeliveryReporting: false,
            allowContentSelectionDeliveryReporting: false,
            allowMeasurement: false,
        } as GdprAdsPrefsOptions,
        [OwsOptionsFeatureType.CalendarSurfaceAddins]: {
            feature: OwsOptionsFeatureType.CalendarSurfaceAddins,
            calendarSurfaceAddins: [],
        } as CalendarSurfaceAddinsOptions,
        [OwsOptionsFeatureType.ExternalImages]: {
            feature: OwsOptionsFeatureType.ExternalImages,
            externalImagesSelectedOption: null,
        } as ExternalImagesOptions,
        [OwsOptionsFeatureType.EventCapture]: {
            autoCollectionEnabled: false,
        } as EventCaptureOptions,
        [OwsOptionsFeatureType.LinkedInBindingDismissed]: {
            linkedInBindingDismissedInPeoplePicker: false,
        } as LinkedInBindingDismissedOptions,
        [OwsOptionsFeatureType.Translation]: {
            feature: OwsOptionsFeatureType.Translation,
            translationMode: 0,
            excludedLanguages: [],
        } as TranslationOptions,
        [OwsOptionsFeatureType.CalendarSurfaceOptions]: {
            feature: OwsOptionsFeatureType.CalendarSurfaceOptions,
            agendaPaneIsClosed: true /** defaut agendaPaneIsClosed to true as we want the panel
            to remain closed until the updated option value is fetched */,
        } as CalendarSurfaceOptions,
        [OwsOptionsFeatureType.MentionEventNotifications]: {
            feature: OwsOptionsFeatureType.MentionEventNotifications,
            enabled: false,
        } as MentionEventNotificationsOptions,
        [OwsOptionsFeatureType.TxpEventNotifications]: {
            feature: OwsOptionsFeatureType.TxpEventNotifications,
            enabled: false,
        } as TxpEventNotificationsOptions,
    },
    loadState: LoadState.OptionsNotLoaded,
});
